import { useState } from 'react';

// ------------------------------------------------------------------
// ReportMediaSummaryGrid — a report's photo evidence, as one scannable
// grid (story 4-81; P06 §4). Story 4-119 (Media Tools) extends this SAME
// primitive with layout/image-fit/caption/timestamp/grouping controls
// rather than building a second one — see this file's own per-prop
// comments below for what each control does and, where relevant, why it
// resolves to today's unchanged behavior by default.
//
// `ReportContent.mediaSummary` (story 4-80) is sourced from
// `inspection.rooms[].photos[]`, the same collection the per-room sections
// already count, so this grid is a second VIEW of evidence that already
// appears inline per section — a reader wants both "what did this room
// look like" (inline) and "show me every photo at a glance" (this block).
//
// a11y: a `<figure>` per photo with a real `<figcaption>` — room name and
// caption both, when present, joined into one sentence rather than two
// silent `<span>`s a screen reader would read with no relation to each
// other. A tile with no `thumbnailUrl`, OR one whose image fails to load
// (story 4-119 — `onError`, the same graceful-degradation idea
// `pdfExport.service.js#logoImgWithFallback` already established for a
// broken letterhead logo, story 4-107), renders a plain labelled
// placeholder instead of a broken `<img>`.
//
// `thumbnailSize` reads the template's saved "Report layout" panel choice
// (`ReportContent.style.thumbnailSize`, story 4-76) — the same field the
// PDF pipeline already threads through for photo sizing. Before this fix
// the grid ignored it and always rendered the same fixed tile count; a
// template author choosing "large" thumbnails now actually gets fewer,
// bigger tiles here, not just in the per-section photo count.
//
// Scope boundary (story 4-119, read before extending this file further):
// this is the report-WIDE "Media summary" rollup grid — settings here
// (`layout`/`imageFit`/`showCaptions`/`showTimestamps`/`groupBy`) are a
// DIFFERENT, unrelated mechanism from the per-SECTION `evidencePlacement`
// (`inline`/`afterResponse`/`gallery`/`appendix`, stories 4-116/4-117),
// which governs where ONE section's OWN inline photos render. Both happen
// to use the string `'appendix'` for one value — a naming coincidence, not
// a shared mechanism; see `MediaSummaryLayout`'s own comment
// (`agents/shared/formSchema.d.ts`) for the full reasoning.
export interface ReportMediaSummaryPhoto {
  roomName: string | null;
  thumbnailUrl: string | null;
  caption: string | null;
  /** Story 4-119 — the question this photo answers (modern
   *  `inspectionEvidence` photos only); `null` for a legacy room photo,
   *  which answers no question. The real reference `groupBy: 'question'`
   *  groups on. */
  fieldLabel?: string | null;
  /** Story 4-119 — that field's own section name; `null` for a legacy room
   *  photo or a re-pinned template's since-removed field. The real
   *  reference `groupBy: 'section'` groups on. */
  sectionName?: string | null;
  /** Story 4-119 — when the photo was captured (ISO string), `null` when
   *  the source recorded none. Shown only when `showTimestamps` is on. */
  capturedAt?: string | null;
}

export type ReportMediaThumbnailSize = 'small' | 'medium' | 'large';
export type ReportMediaSummaryLayout = 'grid' | 'list' | 'fullWidth' | 'appendix';
export type ReportMediaSummaryImageFit = 'cover' | 'contain';
export type ReportMediaSummaryGroupBy = 'none' | 'question' | 'section' | 'finding';

const GRID_COLS: Record<ReportMediaThumbnailSize, string> = {
  small: 'grid-cols-3 sm:grid-cols-4 md:grid-cols-6',
  medium: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
  large: 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3',
};

// Story 4-119 — same page-safety reasoning as `pdfExport.service.js
// #MEDIA_TILE_MAX_HEIGHT_MM` (kept in lockstep by hand, same value): a FIXED
// box height for the `fullWidth`/`list` layouts, so a genuinely oversized or
// oddly-proportioned source image is always scaled DOWN to fit rather than
// growing the tile. `grid`'s pre-existing `aspect-square` already has this
// property for free (a square box regardless of source dimensions).
const FULL_WIDTH_MAX_HEIGHT = '100mm';
const LIST_TILE_SIZE = '30mm';

export interface ReportMediaSummaryGridProps {
  totalPhotos: number;
  roomsWithPhotos: number;
  photos: ReportMediaSummaryPhoto[];
  /** The template's saved thumbnail size. Defaults to `medium` — the panel's own default. Applies to the `grid`/`appendix` layouts only (`list`/`fullWidth` use their own fixed tile size). */
  thumbnailSize?: ReportMediaThumbnailSize;
  className?: string;
  /** Story 4-119 — tile arrangement. `'appendix'` renders identically to
   *  `'grid'` here (it is a POSITION choice the caller, `ReportRender.tsx`,
   *  already acted on before mounting this component — see that file's own
   *  comment). Default `'grid'`, byte-identical to every pre-4-119 render. */
  layout?: ReportMediaSummaryLayout;
  /** Story 4-119 — `object-fit`. Default `'cover'` (today's unchanged crop-to-fill behavior). */
  imageFit?: ReportMediaSummaryImageFit;
  /** Story 4-119 — default `true` (today's unchanged always-on caption). */
  showCaptions?: boolean;
  /** Story 4-119 — default `false` (no pre-4-119 render ever showed one). */
  showTimestamps?: boolean;
  /** Story 4-119 — default `'none'` (today's unchanged flat grid, no group headings). */
  groupBy?: ReportMediaSummaryGroupBy;
  /** Story 4-117 — additive, default `'area'` (byte-identical to every
   *  pre-4-117 caller). The new report-level "Supporting documents" appendix
   *  block (`ReportRender.tsx#appendixPhotosOf`) reuses this SAME primitive
   *  with `groupNoun="section"` rather than a second grid, so its count line
   *  reads "N photos across M sections" instead of "...areas". */
  groupNoun?: string;
}

const captionOf = (photo: ReportMediaSummaryPhoto): string =>
  [photo.roomName, photo.caption].filter(Boolean).join(' — ') || 'Photo';

/**
 * Real bug, found live (2026-09-08) — "16 photos across 0 areas".
 * `pdfExport.service.js#mediaSummaryCountLine` is the full write-up (that
 * file's own comment) of why `roomsWithPhotos` is legitimately 0 for a
 * template whose photos all come from field-level evidence rather than
 * legacy `inspection.rooms[]` — the DATA is correct, `report.service.js
 * #mediaSummaryOf`'s own pinned tests say so explicitly. The COPY is what
 * needs fixing: printing "across 0 areas" states a real number as if it
 * meant something. Falls back to counting distinct `sectionName`s among the
 * photos themselves (every evidence-sourced photo carries its real section),
 * switching the noun to "section"; if that is ALSO zero, drops the
 * "across..." clause rather than print another hollow zero. Byte-identical
 * twin of the PDF fix — same bug, same fix, both renderers.
 */
const mediaSummaryCountLine = (
  totalPhotos: number,
  roomsWithPhotos: number,
  photos: ReportMediaSummaryPhoto[],
  groupNoun: string,
): string => {
  const photoWord = totalPhotos === 1 ? 'photo' : 'photos';
  let count = roomsWithPhotos;
  let noun = groupNoun;
  if (!count) {
    count = new Set(photos.map((p) => p.sectionName).filter(Boolean)).size;
    noun = 'section';
  }
  if (!count) return `${totalPhotos} ${photoWord}`;
  return `${totalPhotos} ${photoWord} across ${count} ${count === 1 ? noun : `${noun}s`}`;
};

/**
 * The media tile's capture timestamp.
 *
 * `timeZone: 'UTC'` is load-bearing, and it is the second half of a fix story
 * 4-128f only half made. That story pinned `paginate.ts#footerShortDate`'s
 * LOCALE because a bare `toLocaleDateString()` resolves against the host's own
 * default — "two hosts, two answers, for a value P06 §4.1 requires to be
 * identical". Pinning the locale alone is not enough: `toLocaleString` also
 * resolves against the host's TIME ZONE, and this line prints an hour.
 *
 * Story 4-130's matrix found it live, with the two hosts disagreeing by ten
 * hours on the same photo (defect D-14):
 *
 *   frontend (macOS, Australia/Brisbane)  "Evidence 1 — north elevation Mar 3, 2026, 8:15 PM"
 *   worker   (Alpine container, UTC)      "Evidence 1 — north elevation Mar 3, 2026, 9:15 AM"
 *
 * UTC, not "the tenant's zone", because the render input carries no timezone
 * and inventing one here would put a second policy owner inside the document.
 * When a tenant timezone reaches `ReportContent` it belongs on the content, and
 * this function should read it from there — until then the only defensible
 * answer is the one that is the same everywhere.
 */
const formatCapturedAt = (isoString: string | null | undefined): string | null => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

/**
 * `pdfExport.service.js#groupMediaPhotos`'s own twin — same "question"/
 * "section" real dimensions, same "Ungrouped" bucket for a photo with no
 * resolvable value, sunk to the end. `'finding'` is never passed here in
 * practice (the backend already downgrades it to `'none'` before this
 * component's props are built — `report.service.js
 * #resolveMediaSummaryPresentation`), but this function treats any
 * unrecognised value the same defensive way: one flat, ungrouped bucket.
 */
function groupMediaPhotos(
  photos: ReportMediaSummaryPhoto[],
  groupBy: ReportMediaSummaryGroupBy,
): Array<{ label: string | null; photos: ReportMediaSummaryPhoto[] }> {
  if (groupBy !== 'question' && groupBy !== 'section') return [{ label: null, photos }];
  const keyOf = (photo: ReportMediaSummaryPhoto): string =>
    (groupBy === 'question' ? photo.fieldLabel : photo.sectionName) || 'Ungrouped';
  const order: string[] = [];
  const byLabel = new Map<string, ReportMediaSummaryPhoto[]>();
  photos.forEach((photo) => {
    const label = keyOf(photo);
    if (!byLabel.has(label)) {
      byLabel.set(label, []);
      order.push(label);
    }
    byLabel.get(label)!.push(photo);
  });
  order.sort((a, b) => (a === 'Ungrouped' ? 1 : 0) - (b === 'Ungrouped' ? 1 : 0));
  return order.map((label) => ({ label, photos: byLabel.get(label)! }));
}

interface MediaTileProps {
  photo: ReportMediaSummaryPhoto;
  boxClassName: string;
  boxStyle: { width?: string; height?: string } | undefined;
  imageFit: ReportMediaSummaryImageFit;
  showCaptions: boolean;
  showTimestamps: boolean;
  figureClassName: string;
}

/**
 * One tile. Story 4-119 — `onError` swaps a thumbnail that fails to load
 * (a broken/inaccessible url — DISTINCT from the pre-existing "no
 * `thumbnailUrl` at all" case, which already rendered the placeholder
 * directly) to the SAME honest "No preview" placeholder, mirroring
 * `pdfExport.service.js#logoImgWithFallback`'s established graceful-
 * degradation pattern (story 4-107) applied here to a photo for the first
 * time.
 */
function MediaTile({
  photo,
  boxClassName,
  boxStyle,
  imageFit,
  showCaptions,
  showTimestamps,
  figureClassName,
}: MediaTileProps): JSX.Element {
  const [broken, setBroken] = useState(false);
  const caption = captionOf(photo);
  const timestamp = showTimestamps ? formatCapturedAt(photo.capturedAt) : null;
  const showImage = Boolean(photo.thumbnailUrl) && !broken;
  return (
    <figure className={figureClassName}>
      <div className={boxClassName} style={boxStyle}>
        {showImage ? (
          <img
            src={photo.thumbnailUrl!}
            alt={caption}
            className="h-full w-full"
            style={{ objectFit: imageFit }}
            /* Story 4-128e, defect D-12 — `loading="lazy"` REMOVED, not
               reformatted. It deadlocked the shared pagination adapter: Paged.js
               awaits every `<img>` in the fragmented pages to report `complete`
               before it resolves, and a lazy image that is off-screen — which
               every page of a paginated document is, and which a hidden one
               always is — never begins loading, so `complete` stays false
               forever. Measured in the worker's own pooled Chromium: ONE photo
               whose URL fails to load (a deleted storage object, a rotated
               bucket URL, an expired signed URL) produced all four pages and
               then never resolved, i.e. a report stuck "generating" for good.
               Deferred loading is meaningless in a print document anyway: the
               fragmenter has to measure every image to place every page, so
               there is nothing to defer. Full measurement table:
               `docs/prd/reference/4-128-report-renderer/defects.md` D-12. */
            onError={() => setBroken(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-caption text-muted-foreground">
            No preview
          </div>
        )}
      </div>
      {showCaptions && (
        <figcaption className="truncate text-caption text-muted-foreground" title={caption}>
          {caption}
        </figcaption>
      )}
      {timestamp && <p className="text-caption text-muted-foreground">{timestamp}</p>}
    </figure>
  );
}

export function ReportMediaSummaryGrid({
  totalPhotos,
  roomsWithPhotos,
  photos,
  thumbnailSize = 'medium',
  className = '',
  layout = 'grid',
  imageFit = 'cover',
  showCaptions = true,
  showTimestamps = false,
  groupBy = 'none',
  groupNoun = 'area',
}: ReportMediaSummaryGridProps): JSX.Element {
  const isList = layout === 'list';
  const isFullWidth = layout === 'fullWidth';
  const boxClassName = isList
    ? 'overflow-hidden rounded-lg border border-border/60 bg-muted flex-none'
    : isFullWidth
      ? 'w-full overflow-hidden rounded-lg border border-border/60 bg-muted'
      : 'aspect-square overflow-hidden rounded-lg border border-border/60 bg-muted';
  const boxStyle = isList
    ? { width: LIST_TILE_SIZE, height: LIST_TILE_SIZE }
    : isFullWidth
      ? { height: FULL_WIDTH_MAX_HEIGHT }
      : undefined;
  const figureClassName = isList ? 'flex flex-row items-start gap-3 break-inside-avoid' : 'space-y-1 break-inside-avoid';
  const containerClassName = isList
    ? 'flex flex-col gap-3'
    : isFullWidth
      ? 'flex flex-col gap-4'
      : `grid gap-3 ${GRID_COLS[thumbnailSize]}`;

  const renderTiles = (groupPhotos: ReportMediaSummaryPhoto[]): JSX.Element => (
    <div className={containerClassName}>
      {groupPhotos.map((photo, index) => (
        <MediaTile
          key={`${photo.thumbnailUrl ?? 'photo'}-${index}`}
          photo={photo}
          boxClassName={boxClassName}
          boxStyle={boxStyle}
          imageFit={imageFit}
          showCaptions={showCaptions}
          showTimestamps={showTimestamps}
          figureClassName={figureClassName}
        />
      ))}
    </div>
  );

  return (
    <div className={`space-y-2 ${className}`}>
      <p className="text-xs text-muted-foreground">{mediaSummaryCountLine(totalPhotos, roomsWithPhotos, photos, groupNoun)}</p>
      {groupMediaPhotos(photos, groupBy).map(({ label, photos: groupPhotos }, groupIndex) =>
        label == null ? (
          <div key="ungrouped">{renderTiles(groupPhotos)}</div>
        ) : (
          <div key={`${label}-${groupIndex}`} className="space-y-2">
            <p className="text-caption font-semibold uppercase tracking-wide text-muted-foreground">
              {label} ({groupPhotos.length})
            </p>
            {renderTiles(groupPhotos)}
          </div>
        ),
      )}
    </div>
  );
}
